import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Coins,
  Sparkles,
  ShoppingBag,
  Factory,
  Store,
  BadgePercent,
  Compass,
  ExternalLink,
  Circle,
  Layers,
  BatteryCharging,
} from "lucide-react";
import Navbar from "../components/Navbar";
import PurchaseWizard from "../components/PurchaseWizard";
import IbjaChart from "../components/IbjaChart";
import { api } from "../lib/api";
import { fmtMoney, fmtPct, marginColor, displayTicker } from "../lib/format";

const METAL_META = {
  gold: {
    label: "Gold",
    Icon: Coins,
    color: "text-amber_brand",
    bg: "bg-amber_brand-bg",
    ring: "ring-amber_brand/30",
  },
  silver: {
    label: "Silver",
    Icon: Coins,
    color: "text-slate-500",
    bg: "bg-slate-100",
    ring: "ring-slate-300/60",
  },
  copper: {
    label: "Copper",
    Icon: Circle,
    color: "text-orange-500",
    bg: "bg-orange-50",
    ring: "ring-orange-200",
  },
  aluminum: {
    label: "Aluminum",
    Icon: Layers,
    color: "text-zinc-500",
    bg: "bg-zinc-100",
    ring: "ring-zinc-300",
  },
  growth_metals: {
    label: "Growth Metals · Li · Rare Earth · Zn",
    Icon: BatteryCharging,
    color: "text-moss",
    bg: "bg-moss-bg/50",
    ring: "ring-moss/30",
  },
};

const MetalGlyph = ({ metal, size = "md" }) => {
  const meta = METAL_META[metal];
  if (!meta) return null;
  const s = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";
  const box =
    size === "sm"
      ? "w-6 h-6"
      : size === "lg"
        ? "w-9 h-9"
        : "w-7 h-7";
  return (
    <span
      className={`inline-flex items-center justify-center rounded-sm ${box} ${meta.bg} ${meta.color} ring-1 ${meta.ring} flex-shrink-0`}
      aria-hidden="true"
    >
      <meta.Icon className={s} strokeWidth={2} />
    </span>
  );
};

export default function Metals() {
  const [prices, setPrices] = useState(null);
  const [outlook, setOutlook] = useState(null);
  const [outlookLoading, setOutlookLoading] = useState(true);
  const [stocks, setStocks] = useState(null);
  const [guide, setGuide] = useState(null);
  const [guideRegion, setGuideRegion] = useState("india");
  const [retail, setRetail] = useState(null);
  const [offers, setOffers] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [stockFilter, setStockFilter] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/metals/prices", { timeout: 60000 }).then((r) => setPrices(r.data)).catch(() => {});
    api.get("/metals/stocks", { timeout: 120000 }).then((r) => setStocks(r.data)).catch(() => {});
    api.get("/metals/buying-guide").then((r) => setGuide(r.data.guide)).catch(() => {});
    api.get("/metals/retail", { timeout: 90000 }).then((r) => setRetail(r.data)).catch(() => {});
    api.get("/metals/offers", { timeout: 120000 }).then((r) => setOffers(r.data)).catch(() => {});
    api
      .get("/metals/outlook", { timeout: 120000 })
      .then((r) => setOutlook(r.data))
      .catch(() => {})
      .finally(() => setOutlookLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-bone-50" data-testid="metals-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <div className="overline mb-3 flex items-center gap-1.5">
            <Coins className="w-3.5 h-3.5" />— Metals Desk · India + World
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            Gold, Silver &amp; growth metals
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Live prices and expert projections. Buy <span className="font-mono text-moss">physical gold &amp; silver</span> —
            play copper, aluminum and battery metals through <span className="font-mono text-moss">producer stocks</span>.
          </p>
        </div>

        {/* Live prices */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-testid="metals-prices">
          {["gold", "silver", "copper", "aluminum"].map((m) => {
            const p = prices?.metals?.[m];
            const filterable = m !== "gold";
            return (
              <div
                key={m}
                onClick={() => filterable && setStockFilter(stockFilter === m ? null : m)}
                className={`bg-white border rounded-sm p-5 transition-colors ${
                  filterable ? "cursor-pointer hover:border-moss/60" : ""
                } ${stockFilter === m ? "border-moss ring-1 ring-moss/40" : "border-line"}`}
                data-testid={`metal-price-${m}`}
              >
                <div className="overline mb-1.5 flex items-center gap-1.5"><MetalGlyph metal={m} size="sm" /> {METAL_META[m].label}{filterable && <span className="text-stone_brand-light"> · tap to filter stocks</span>}</div>
                {!p ? (
                  <div className="h-10 bg-bone-100 rounded-sm animate-pulse" />
                ) : (
                  <>
                    <div className="font-mono text-xl text-stone_brand-dark">
                      ${p.usd_price?.toLocaleString()} <span className="text-[0.6rem] text-stone_brand-light">{p.usd_unit}</span>
                    </div>
                    {p.inr_price && (
                      <div className="font-mono text-sm text-stone_brand">
                        ₹{Math.round(p.inr_price).toLocaleString("en-IN")} <span className="text-[0.6rem] text-stone_brand-light">{p.inr_unit}</span>
                      </div>
                    )}
                    <div className={`font-mono text-xs mt-1 ${marginColor(p.day_change_percent)}`}>{fmtPct(p.day_change_percent)} today</div>
                    {p.stale && <div className="text-[0.55rem] font-mono text-amber_brand" data-testid={`stale-${m}`}>last close · live feed busy</div>}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Real shop rates — India */}
        <section className="mb-10" data-testid="metals-retail">
          <div className="overline mb-3 flex items-center gap-1.5 text-moss">
            <Store className="w-3.5 h-3.5" />— Real shop rates today (Indian jeweller board rates)
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            <div className="bg-white border border-line rounded-sm p-5">
              {!retail ? (
                <div className="h-40 bg-bone-100 rounded-sm animate-pulse" />
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    {retail.national?.gold_22k_per_gram && (
                      <div>
                        <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">22K Gold · shop</div>
                        <div className="font-mono text-xl text-stone_brand-dark">₹{Math.round(retail.national.gold_22k_per_gram).toLocaleString("en-IN")}<span className="text-[0.6rem] text-stone_brand-light">/gm</span></div>
                      </div>
                    )}
                    {retail.national?.silver_per_kg && (
                      <div>
                        <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">Silver · shop</div>
                        <div className="font-mono text-xl text-stone_brand-dark">₹{Math.round(retail.national.silver_per_kg).toLocaleString("en-IN")}<span className="text-[0.6rem] text-stone_brand-light">/kg</span></div>
                      </div>
                    )}
                    {retail.retail_premium_over_spot_pct !== null && retail.retail_premium_over_spot_pct !== undefined && (
                      <div className="ml-auto text-right">
                        <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">Shop premium vs spot</div>
                        <div className="font-mono text-xl text-amber_brand font-semibold" data-testid="retail-premium">+{retail.retail_premium_over_spot_pct}%</div>
                      </div>
                    )}
                  </div>
                  <table className="w-full" data-testid="retail-city-table">
                    <thead>
                      <tr className="border-b border-line text-left">
                        <th className="overline py-2 text-stone_brand-light">City</th>
                        <th className="overline py-2 text-stone_brand-light text-right">22K ₹/gm</th>
                        <th className="overline py-2 text-stone_brand-light text-right">24K ₹/gm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(retail.cities || []).map((c) => (
                        <tr key={c.city} className="border-b border-line last:border-0">
                          <td className="py-1.5 text-sm text-stone_brand-dark">{c.city}</td>
                          <td className="py-1.5 font-mono text-sm text-stone_brand-dark text-right">{c.gold_22k_per_gram ? `₹${Math.round(c.gold_22k_per_gram).toLocaleString("en-IN")}` : "—"}</td>
                          <td className="py-1.5 font-mono text-sm text-stone_brand-dark text-right">{c.gold_24k_per_gram ? `₹${Math.round(c.gold_24k_per_gram).toLocaleString("en-IN")}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light mt-2">— {retail.source} · refreshed hourly · use as your negotiation benchmark</p>
                </>
              )}
            </div>

            {/* Official IBJA chart + your prices + expert lines */}
            <IbjaChart />
          </div>
        </section>

        {/* Online offers */}
        <section className="mb-10" data-testid="metals-offers">
          <div className="overline mb-3 flex items-center gap-1.5 text-amber_brand">
            <BadgePercent className="w-3.5 h-3.5" />— Offers available online right now
          </div>
          {!offers ? (
            <div className="h-28 bg-white border border-line rounded-sm animate-pulse" />
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(offers.offers || []).map((o, i) => (
                  <a
                    key={i}
                    href={o.link}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-white border border-line rounded-sm p-4 hover:border-moss/50 transition-colors group"
                    data-testid={`offer-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-stone_brand-dark group-hover:text-moss transition-colors">{o.platform}</span>
                      <span className="flex items-center gap-1 text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1.5 py-0.5 rounded-sm">
                        {o.region} <ExternalLink className="w-2.5 h-2.5" />
                      </span>
                    </div>
                    <p className="text-xs text-moss font-medium mb-1">{o.offer}</p>
                    <p className="text-[0.7rem] text-stone_brand">{o.how_to_claim}</p>
                  </a>
                ))}
              </div>
              {(offers.tips || []).length > 0 && (
                <p className="text-[0.7rem] text-stone_brand mt-2">
                  <span className="font-mono uppercase tracking-widest text-stone_brand-light">Timing tips:</span> {offers.tips.join(" · ")}
                </p>
              )}
              <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light mt-1">— {offers.note}</p>
            </>
          )}
        </section>

        {/* Expert outlook */}
        <section className="mb-10" data-testid="metals-outlook">
          <div className="overline mb-3 flex items-center gap-1.5 text-moss">
            <Sparkles className="w-3.5 h-3.5" />— Expert projections to 2030 (AI-curated)
          </div>
          {outlookLoading ? (
            <div className="grid md:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-40 bg-white border border-line rounded-sm animate-pulse" />)}
            </div>
          ) : !outlook ? (
            <div className="bg-white border border-line rounded-sm p-6 text-sm text-stone_brand">Outlook unavailable right now — refresh in a minute.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {["gold", "silver", "copper", "aluminum"].map((m) => {
                const o = outlook.outlook?.[m];
                if (!o) return null;
                return (
                  <div key={m} className="bg-white border border-line rounded-sm p-5" data-testid={`outlook-${m}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-serif-display text-lg text-stone_brand-dark font-medium flex items-center gap-2"><MetalGlyph metal={m} /> {METAL_META[m].label}</div>
                      <div className="font-mono text-sm text-moss font-semibold">2030: {o.target_2030}</div>
                    </div>
                    <p className="text-sm text-stone_brand mb-3">{o.view}</p>
                    <div className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light mb-1">Drivers</div>
                    <ul className="text-xs text-stone_brand mb-3 space-y-0.5">
                      {(o.drivers || []).map((d, i) => <li key={i}>· {d}</li>)}
                    </ul>
                    <div className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light mb-1">Street views</div>
                    <ul className="text-xs text-stone_brand space-y-0.5">
                      {(o.expert_views || []).map((v, i) => (
                        <li key={i}><span className="font-semibold text-stone_brand-dark">{v.firm}:</span> {v.take}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light mt-2">
            — AI-curated synthesis of publicly reported analyst views · verify before acting
          </p>
        </section>

        {/* Physical buying guide — gold & silver only */}
        <section className="mb-10" data-testid="metals-buying-guide">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="overline flex items-center gap-1.5 text-moss">
              <ShoppingBag className="w-3.5 h-3.5" />— Where &amp; how to buy PHYSICAL gold &amp; silver
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWizard("gold")}
                className="flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest bg-moss text-bone-50 hover:bg-moss-hover transition-colors"
                data-testid="open-gold-wizard-btn"
              >
                <Compass className="w-3.5 h-3.5" /> Assisted purchase — step by step
              </button>
              <div className="flex items-center gap-1.5">
              {[{ k: "india", l: "India", sub: "IN" }, { k: "world", l: "World", sub: "GL" }].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setGuideRegion(t.k)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-mono tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                    guideRegion === t.k ? "bg-stone_brand-dark text-bone-50" : "bg-white border border-line text-stone_brand hover:border-moss/50"
                  }`}
                  data-testid={`guide-region-${t.k}`}
                >
                  <span className="text-[0.6rem] font-semibold opacity-60">{t.sub}</span>
                  {t.l}
                </button>
              ))}
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            {["gold", "silver"].map((m) => (
              <div key={m} className="bg-white border border-line rounded-sm overflow-hidden" data-testid={`buying-guide-${m}`}>
                <div className="px-5 py-3 border-b border-line bg-bone-100/60 font-serif-display text-lg text-stone_brand-dark font-medium">
                  <MetalGlyph metal={m} size="sm" /> Physical {METAL_META[m].label}
                </div>
                <ul className="divide-y divide-line">
                  {(guide?.[m]?.[guideRegion] || []).map((g, i) => (
                    <li key={i} className="px-5 py-3.5">
                      <div className="text-sm font-semibold text-stone_brand-dark mb-0.5">{g.channel}</div>
                      <p className="text-xs text-stone_brand mb-1">{g.how}</p>
                      <p className="text-[0.7rem] font-mono text-moss">→ {g.tip}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Metal producer stocks */}
        <section data-testid="metals-stocks">
          <div className="overline mb-3 flex flex-wrap items-center gap-2 text-moss">
            <Factory className="w-3.5 h-3.5" />— Producer stocks: silver · copper · aluminum · growth metals
            {stockFilter && (
              <button
                onClick={() => setStockFilter(null)}
                className="ml-2 px-2 py-0.5 rounded-sm text-[0.6rem] font-mono bg-moss text-bone-50 hover:bg-moss-hover"
                data-testid="stock-filter-clear"
              >
                Filtered: {stockFilter} ✕ show all
              </button>
            )}
          </div>
          {!stocks ? (
            <div className="grid gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 bg-white border border-line rounded-sm animate-pulse" />)}</div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-3">
              {Object.entries(stocks.groups || {})
                .filter(([metal]) => !stockFilter || metal === stockFilter)
                .map(([metal, rows]) => (
                <div key={metal} className="bg-white border border-line rounded-sm overflow-hidden" data-testid={`metal-stocks-${metal}`}>
                  <div className="px-5 py-3 border-b border-line bg-bone-100/60 font-serif-display text-lg text-stone_brand-dark font-medium">
                    <MetalGlyph metal={metal} size="sm" /> {METAL_META[metal]?.label || metal}
                  </div>
                  <table className="w-full">
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.ticker}
                          className="border-b border-line last:border-0 hover:bg-bone-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/stock/${encodeURIComponent(r.ticker)}`)}
                          data-testid={`metal-stock-${r.ticker}`}
                        >
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-semibold text-stone_brand-dark">{displayTicker(r.ticker)}</span>
                              <span className="text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                                {r.region}
                              </span>
                            </div>
                            <div className="text-[0.7rem] text-stone_brand truncate max-w-[200px]">{r.company_name}</div>
                          </td>
                          <td className="py-2.5 px-3 font-mono text-sm text-stone_brand-dark text-right">
                            {fmtMoney(r.current_price, r.currency)}
                            <div className={`text-[0.65rem] ${marginColor(r.day_change_percent)}`}>{fmtPct(r.day_change_percent)}</div>
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            <div className={`font-mono text-sm font-semibold ${marginColor(r.upside_percent)}`}>{fmtPct(r.upside_percent)}</div>
                            <div className="text-[0.55rem] font-mono uppercase tracking-widest text-stone_brand-light">vs target</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      {wizard && <PurchaseWizard type={wizard} onClose={() => setWizard(null)} />}
    </div>
  );
}
